/**
 * Pack-station state machine (§14.1) — the crown jewel, kept PURE so it
 * typechecks and unit-tests with no React/IO. Imports only @cairn/core
 * (checkWeight) and @cairn/shared (domain DTOs, enums) + node builtins.
 *
 * Implements: IDLE → VERIFYING → WEIGHING → LABELLING → DONE, plus the
 * label-failure path (§14.1 / §42.2-4), parking (§14.3), multi-box (§14.2),
 * mark-short (§32/§35), unpack a single item (§33.5), case-insensitive/trim
 * matching with barcode aliases (§29.7/§29.2), over-scan and wrong-item
 * handling (§14.1/§29.7), manual confirm (§14.1), and weight override (§14.1).
 *
 * The reducer is `reduce(state, event) -> state`: a single pure function so the
 * React layer (PackStation.tsx) is a thin renderer + async-persistence shell.
 * Non-shippable / informational lines (§29.10) are excluded from the scan
 * checklist and the weight expectation. Bundle parents (§29.3) are not scanned
 * directly; their components are. Both are filtered out of `packableLines`.
 *
 * §15/§33.1/§38/§40 (Pack-workflow Task 6) — the bench loop is shared by all
 * three entry points (quick-pack, batch label-first, batch one-by-one). The mode
 * changes ONLY when the label prints: PACK_FIRST creates the label at the bench
 * (the existing LABELLING step); LABEL_FIRST matches the order to its pre-printed
 * label (MATCHING phase, §33.1) and treats the weigh as a discrepancy check vs the
 * theoretical weight the label was printed with (§15) — no carrier call at the
 * bench. The extension is ADDITIVE: with the default PACK_FIRST mode the reducer
 * behaves exactly as before, so the original tests are unchanged.
 */
import type {
  FulfilmentMode,
  OrderDTO,
  OrderLineDTO,
  ShortageReasonCode,
  WeightFlag,
} from "@cairn/shared";
import { checkWeight, type WeightCheckResult, type WeightTolerance } from "@cairn/core";

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export type PackPhase =
  | "IDLE"
  | "VERIFYING"
  | "MATCHING" // §33.1 LABEL_FIRST — confirm the order↔pre-printed-label pairing
  | "WEIGHING"
  | "LABELLING"
  | "DONE"
  | "LABEL_FAILED"; // §14.1 — failure surfaced at the bench, pack work preserved

/** The most recent feedback signal — drives the audio cue + flash in the UI. */
export type PackCue =
  | "NONE"
  | "ITEM_OK" // positive beep (§14.1)
  | "LINE_COMPLETE" // line turned green (§14.1)
  | "WRONG_ITEM" // red flash + error sound (§14.1/§29.7)
  | "OVER_SCAN" // amber "Already packed n/n" (§14.1)
  | "WEIGHT_OK"
  | "WEIGHT_FAIL"
  | "WEIGHT_DISCREPANCY" // §15 LABEL_FIRST — actual ≠ theoretical; non-blocking, logged
  | "LABEL_MATCHED" // §33.1 — scanned label barcode matches this order
  | "LABEL_MISMATCH" // §33.1 — wrong label on wrong box (the primary label-first failure)
  | "LABEL_PRINTED"
  | "LABEL_FAILED"
  | "SHORT"
  | "PARKED";

/** Barcode aliases per SKU (§29.2). Empty when a SKU has only its primary. */
export interface SkuBarcodeAlias {
  sku: string;
  barcode: string;
}

/** Per-line packed-count tracking, keyed by OrderLine.id. */
export interface LinePackState {
  lineId: string;
  /** Required quantity to scan (line.quantity). */
  required: number;
  /** Units verified so far this session. */
  packed: number;
  /** §32 mark-short: how many were physically found (null = not marked short). */
  foundQuantity: number | null;
  shortReasonCode: ShortageReasonCode | null;
  /** Which box each packed unit went into (§14.2). Length === packed. */
  boxAssignments: number[];
  /** True when verified via manual confirm rather than a real scan (§14.1). */
  manualConfirmed: boolean;
}

/** A physical box in a (possibly multi-box) shipment (§14.2). */
export interface BoxState {
  index: number; // 1-based parcelIndex
  /** Box tare in grams (from the scanned/selected box barcode → tare). */
  tareG: number;
  /** Last weight check for this box (null until weighed). */
  weight: WeightCheckResult | null;
  weightFlag: WeightFlag | null;
}

export interface PackState {
  phase: PackPhase;
  cue: PackCue;
  /** Human-readable message for the current cue (e.g. wrong-item detail §29.7). */
  message: string | null;
  /** Loaded order; null in IDLE / after RESET. */
  order: OrderDTO | null;
  /**
   * §15 — fulfilment mode of THIS order/batch. The mode changes only when the
   * label prints: PACK_FIRST creates the label at the bench (LABELLING);
   * LABEL_FIRST matches a pre-printed label (MATCHING) and never calls the
   * carrier here. Defaults to PACK_FIRST so the legacy path is unchanged.
   */
  mode: FulfilmentMode;
  /**
   * §33.1 — for LABEL_FIRST, the tracking number on the pre-printed label the
   * packer must confirm against (null for PACK_FIRST / quick-pack).
   */
  expectedTracking: string | null;
  /** §33.1 — true once the pre-printed label has been confirmed for this order. */
  labelMatched: boolean;
  /**
   * §15 — LABEL_FIRST discrepancy flag: the weighed parcel is outside tolerance
   * vs the theoretical weight the label was printed with. NON-blocking (the label
   * already exists) — surfaced + logged; supervisor may void+reprint.
   */
  weightDiscrepancy: boolean;
  /** Station the order is locked to (§4.4). */
  stationId: string | null;
  lines: Record<string, LinePackState>;
  /**
   * Internal lookup: normalized barcode -> set of normalized SKUs that accept
   * it (§29.2 aliases). Part of the state so the reducer stays pure (no module
   * globals); the UI never reads it directly.
   */
  aliasIndex: Map<string, Set<string>>;
  boxes: BoxState[];
  /** Index (1-based) of the box currently receiving scans (§14.2). */
  currentBox: number;
  /** Weight tolerance resolved from settings (§21). */
  tolerance: WeightTolerance;
  /** True once a WEIGHT_OVERRIDE was applied (§14.1, sets WeightFlag.MANUAL). */
  weightOverridden: boolean;
  /** Tracking shown on the DONE screen (§14.1). */
  trackingNumber: string | null;
  /** Last carrier label error (LABEL_FAILED phase). */
  labelError: string | null;
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export type PackEvent =
  | {
      type: "SCAN_SLIP";
      order: OrderDTO;
      stationId: string;
      aliases?: SkuBarcodeAlias[];
      /** §15 — fulfilment mode for this order/batch. Omitted → PACK_FIRST. */
      mode?: FulfilmentMode;
      /** §33.1 — pre-printed label tracking to confirm (LABEL_FIRST only). */
      expectedTracking?: string | null;
    }
  | { type: "SCAN_ITEM"; code: string }
  | { type: "CONFIRM_LABEL"; scan: string } // §33.1 — confirm the pre-printed label (LABEL_FIRST)
  | { type: "COMPLETE_PACK" } // §15/§42.2 — finish a LABEL_FIRST order at the bench (no carrier call)
  | { type: "OVER_SCAN"; lineId: string } // explicit over-scan signal (UI may also derive it)
  | { type: "WRONG_ITEM"; code: string; identified?: { title: string; sku: string } | null }
  | { type: "UNPACK_ITEM"; lineId: string } // §33.5
  | { type: "MANUAL_CONFIRM"; lineId: string; reason: string } // §14.1
  | { type: "SET_BOX"; boxIndex: number; tareG: number } // §14.2 — set/add box + tare
  | { type: "WEIGH"; grams: number; boxIndex?: number } // §14.1 — uses checkWeight
  | { type: "WEIGHT_OVERRIDE"; reason: string; supervisorPin?: string } // §14.1
  | { type: "PHOTO_CAPTURED" } // §13.4 auto-capture on weight pass
  | { type: "LABEL_OK"; trackingNumber: string }
  | { type: "LABEL_FAIL"; error: string }
  | { type: "PARK"; reason: string } // §14.3
  | { type: "MARK_SHORT"; lineId: string; foundQuantity: number; reasonCode: ShortageReasonCode } // §32
  | { type: "RESET" };

// ---------------------------------------------------------------------------
// Constants & defaults
// ---------------------------------------------------------------------------

export const DEFAULT_TOLERANCE: WeightTolerance = { percent: 10, floorGrams: 50 };

export function initialState(tolerance: WeightTolerance = DEFAULT_TOLERANCE): PackState {
  return {
    phase: "IDLE",
    cue: "NONE",
    message: null,
    order: null,
    mode: "PACK_FIRST",
    expectedTracking: null,
    labelMatched: false,
    weightDiscrepancy: false,
    stationId: null,
    lines: {},
    aliasIndex: new Map(),
    boxes: [],
    currentBox: 1,
    tolerance,
    weightOverridden: false,
    trackingNumber: null,
    labelError: null,
  };
}

// ---------------------------------------------------------------------------
// Selectors / helpers
// ---------------------------------------------------------------------------

/**
 * Lines that participate in scan verification + weight expectation. Excludes
 * non-shippable/informational lines (§29.10) and bundle parents (§29.3 — the
 * components are scanned, not the wrapper). Components carry quantity.
 */
export function packableLines(order: OrderDTO): OrderLineDTO[] {
  return order.lines.filter((l) => l.shippable && !l.isBundleParent);
}

function normalizeCode(code: string): string {
  // §29.7 — case-insensitive + trim by default.
  return code.trim().toLowerCase();
}

/**
 * Match a scanned code against an order line (§29.7). Accepts the line's primary
 * barcode, its SKU, and any registered barcode alias for that SKU (§29.2).
 * Returns the matched line id or null.
 */
export function matchScan(state: PackState, code: string): string | null {
  if (!state.order) return null;
  const norm = normalizeCode(code);
  if (norm === "") return null;

  const aliasSkus = state.aliasIndex.get(norm); // set of SKUs that alias this barcode

  for (const line of packableLines(state.order)) {
    if (line.barcode && normalizeCode(line.barcode) === norm) return line.id;
    if (line.sku && normalizeCode(line.sku) === norm) return line.id;
    if (line.sku && aliasSkus && aliasSkus.has(normalizeCode(line.sku))) return line.id;
  }
  return null;
}

/** All packable lines fully verified → eligible to advance to WEIGHING (§14.1). */
export function allLinesVerified(state: PackState): boolean {
  if (!state.order) return false;
  const lines = packableLines(state.order);
  if (lines.length === 0) return false;
  return lines.every((l) => {
    const lp = state.lines[l.id];
    if (!lp) return false;
    // A line marked short counts as "resolved" for advance purposes — it is
    // handled by the shortage flow (§32), not by scanning the missing units.
    if (lp.foundQuantity !== null) return lp.packed >= lp.foundQuantity;
    return lp.packed >= lp.required;
  });
}

/**
 * §15 — where VERIFYING advances once every packable line is green. LABEL_FIRST
 * must confirm the pre-printed label first (MATCHING, §33.1); PACK_FIRST goes
 * straight to WEIGHING (the legacy path). Pure helper so the branch is in one
 * place and the default mode keeps the original behaviour.
 */
function phaseAfterVerify(mode: FulfilmentMode): "MATCHING" | "WEIGHING" {
  return mode === "LABEL_FIRST" ? "MATCHING" : "WEIGHING";
}

/** True once a LABEL_FIRST order is ready to complete: label confirmed + every
 *  box weighed (the discrepancy check has run on each parcel). */
export function readyToCompleteLabelFirst(state: PackState): boolean {
  return (
    state.mode === "LABEL_FIRST" &&
    state.labelMatched &&
    state.boxes.length > 0 &&
    state.boxes.every((b) => b.weightFlag !== null)
  );
}

/** Total verified units across all lines (for progress display). */
export function totalPacked(state: PackState): number {
  return Object.values(state.lines).reduce((sum, lp) => sum + lp.packed, 0);
}

/** Total expected units across packable lines. */
export function totalExpected(state: PackState): number {
  return Object.values(state.lines).reduce((sum, lp) => sum + lp.required, 0);
}

/**
 * Expected weight for a given box = items assigned to that box + box tare.
 * Items weight is summed from the lines whose units were placed in this box.
 * For the common single-box case this equals itemsWeightG + tare.
 */
export function expectedBoxWeightG(state: PackState, boxIndex: number): number {
  if (!state.order) return 0;
  const box = state.boxes.find((b) => b.index === boxIndex);
  const tare = box?.tareG ?? 0;
  let items = 0;
  for (const line of packableLines(state.order)) {
    const lp = state.lines[line.id];
    if (!lp) continue;
    const unitsInBox = lp.boxAssignments.filter((b) => b === boxIndex).length;
    items += unitsInBox * (line.unitWeightG ?? 0);
  }
  return items + tare;
}

// ---------------------------------------------------------------------------
// Internal: build the per-line tracking + alias index when an order loads.
// ---------------------------------------------------------------------------

interface LoadedTracking {
  lines: Record<string, LinePackState>;
  aliasIndex: Map<string, Set<string>>; // normalized barcode -> set of normalized SKUs
}

function buildTracking(order: OrderDTO, aliases: SkuBarcodeAlias[]): LoadedTracking {
  const lines: Record<string, LinePackState> = {};
  for (const line of packableLines(order)) {
    lines[line.id] = {
      lineId: line.id,
      required: line.quantity,
      // Resume support (§2.1 crash-only): seed from persisted quantityPacked.
      packed: Math.min(line.quantityPacked, line.quantity),
      foundQuantity: null,
      shortReasonCode: null,
      boxAssignments: Array.from({ length: Math.min(line.quantityPacked, line.quantity) }, () => 1),
      manualConfirmed: false,
    };
  }
  const aliasIndex = new Map<string, Set<string>>();
  for (const a of aliases) {
    const key = normalizeCode(a.barcode);
    const sku = normalizeCode(a.sku);
    const set = aliasIndex.get(key) ?? new Set<string>();
    set.add(sku);
    aliasIndex.set(key, set);
  }
  return { lines, aliasIndex };
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

/**
 * Pure reducer: (state, event) -> new state. Never mutates the input. Illegal
 * events for the current phase are no-ops with an explanatory message (the UI
 * shows nothing changed) rather than throwing — the bench must never crash on a
 * stray scan.
 */
export function reduce(state: PackState, event: PackEvent): PackState {
  switch (event.type) {
    case "RESET":
      return initialState(state.tolerance);

    case "SCAN_SLIP": {
      // Loading a new slip is valid from IDLE or DONE (auto-reset on next slip,
      // §14.1). Ignore mid-pack to avoid clobbering progress.
      if (state.phase !== "IDLE" && state.phase !== "DONE") {
        return { ...state, cue: "WRONG_ITEM", message: "Finish or park the current order first." };
      }
      const { lines, aliasIndex } = buildTracking(event.order, event.aliases ?? []);
      const fresh = initialState(state.tolerance);
      return {
        ...fresh,
        phase: "VERIFYING",
        order: event.order,
        // §15 — the mode (and any pre-printed label) come from the open response;
        // omitted ⇒ PACK_FIRST quick-pack (the legacy bench loop).
        mode: event.mode ?? "PACK_FIRST",
        expectedTracking: event.expectedTracking ?? null,
        stationId: event.stationId,
        lines,
        aliasIndex,
        boxes: [{ index: 1, tareG: 0, weight: null, weightFlag: null }],
        currentBox: 1,
        cue: "NONE",
        message: null,
      };
    }

    case "SCAN_ITEM": {
      if (state.phase !== "VERIFYING") return state;
      const lineId = matchScan(state, event.code);
      if (lineId === null) {
        // Not in this order → wrong item (§14.1). UI may enrich with catalog
        // lookup via the WRONG_ITEM event; this is the pure fallback.
        return { ...state, cue: "WRONG_ITEM", message: "NOT IN THIS ORDER" };
      }
      const lp = state.lines[lineId];
      if (!lp) return state;
      const cap = lp.foundQuantity ?? lp.required;
      if (lp.packed >= cap) {
        // §14.1 over-scan a complete line → amber "Already packed n/n".
        return {
          ...state,
          cue: "OVER_SCAN",
          message: `Already packed ${lp.packed}/${cap}`,
        };
      }
      const nextPacked = lp.packed + 1;
      const updatedLine: LinePackState = {
        ...lp,
        packed: nextPacked,
        boxAssignments: [...lp.boxAssignments, state.currentBox],
      };
      const lines = { ...state.lines, [lineId]: updatedLine };
      const lineComplete = nextPacked >= cap;
      const next: PackState = {
        ...state,
        lines,
        cue: lineComplete ? "LINE_COMPLETE" : "ITEM_OK",
        message: null,
      };
      // §14.1 — all lines green → auto-advance. PACK_FIRST → WEIGHING (legacy);
      // LABEL_FIRST → MATCHING (confirm the pre-printed label first, §33.1).
      if (allLinesVerified(next)) {
        return { ...next, phase: phaseAfterVerify(next.mode) };
      }
      return next;
    }

    case "OVER_SCAN": {
      if (state.phase !== "VERIFYING") return state;
      const lp = state.lines[event.lineId];
      if (!lp) return state;
      const cap = lp.foundQuantity ?? lp.required;
      return { ...state, cue: "OVER_SCAN", message: `Already packed ${lp.packed}/${cap}` };
    }

    case "WRONG_ITEM": {
      if (state.phase !== "VERIFYING") return state;
      // §29.7 — name the scanned item if the catalog lookup resolved it.
      const msg = event.identified
        ? `Scanned ${event.identified.title} (SKU ${event.identified.sku}) — not in this order.`
        : "Unknown barcode — not in this order.";
      return { ...state, cue: "WRONG_ITEM", message: msg };
    }

    case "CONFIRM_LABEL": {
      // §33.1/§38 — LABEL_FIRST: the packer scans the barcode on the pre-printed
      // label (or the packing slip) to confirm the right order↔label pairing.
      // Accept the label's tracking number OR the order's id/name (the slip
      // barcode encodes Order.id, §12.3). A mismatch is the primary label-first
      // failure mode (wrong label on wrong box) → blocking, stays in MATCHING.
      if (state.phase !== "MATCHING" || !state.order) return state;
      const scan = normalizeCode(event.scan);
      if (scan === "") return state;
      const keys = [state.order.id, state.order.name, state.expectedTracking]
        .filter((k): k is string => !!k)
        .map(normalizeCode);
      if (keys.includes(scan)) {
        return {
          ...state,
          phase: "WEIGHING",
          labelMatched: true,
          cue: "LABEL_MATCHED",
          message: null,
        };
      }
      return {
        ...state,
        cue: "LABEL_MISMATCH",
        message: `Label ${event.scan.trim()} does not match ${state.order.name}. Wrong label — do not ship.`,
      };
    }

    case "UNPACK_ITEM": {
      // §33.5 — reverse a single mis-scan without resetting the order. Allowed
      // in VERIFYING, MATCHING (LABEL_FIRST, before weighing) and WEIGHING (to
      // step back and re-check items); always drops back to VERIFYING.
      if (state.phase !== "VERIFYING" && state.phase !== "MATCHING" && state.phase !== "WEIGHING")
        return state;
      const lp = state.lines[event.lineId];
      if (!lp || lp.packed <= 0) return state;
      const boxAssignments = lp.boxAssignments.slice(0, -1);
      const updatedLine: LinePackState = { ...lp, packed: lp.packed - 1, boxAssignments };
      const lines = { ...state.lines, [event.lineId]: updatedLine };
      // Unpacking drops us back to VERIFYING (progress kept) if we'd advanced.
      return { ...state, phase: "VERIFYING", lines, cue: "NONE", message: null };
    }

    case "MANUAL_CONFIRM": {
      // §14.1 — missing/damaged barcode → manual confirm one unit (reason
      // required; supervisor PIN enforced by the UI/server, not here).
      if (state.phase !== "VERIFYING") return state;
      const lp = state.lines[event.lineId];
      if (!lp) return state;
      const cap = lp.foundQuantity ?? lp.required;
      if (lp.packed >= cap) return { ...state, cue: "OVER_SCAN", message: `Already packed ${lp.packed}/${cap}` };
      const nextPacked = lp.packed + 1;
      const updatedLine: LinePackState = {
        ...lp,
        packed: nextPacked,
        manualConfirmed: true,
        boxAssignments: [...lp.boxAssignments, state.currentBox],
      };
      const lines = { ...state.lines, [event.lineId]: updatedLine };
      const next: PackState = { ...state, lines, cue: "ITEM_OK", message: null };
      if (allLinesVerified(next)) return { ...next, phase: phaseAfterVerify(next.mode) };
      return next;
    }

    case "SET_BOX": {
      // §14.2 — set the current box tare, or add a new box. Valid while
      // verifying or weighing.
      if (state.phase !== "VERIFYING" && state.phase !== "WEIGHING") return state;
      const existing = state.boxes.find((b) => b.index === event.boxIndex);
      let boxes: BoxState[];
      if (existing) {
        boxes = state.boxes.map((b) =>
          b.index === event.boxIndex ? { ...b, tareG: event.tareG } : b,
        );
      } else {
        boxes = [
          ...state.boxes,
          { index: event.boxIndex, tareG: event.tareG, weight: null, weightFlag: null },
        ].sort((a, b) => a.index - b.index);
      }
      return { ...state, boxes, currentBox: event.boxIndex, cue: "NONE", message: null };
    }

    case "WEIGH": {
      if (state.phase !== "WEIGHING") return state;
      const boxIndex = event.boxIndex ?? state.currentBox;
      const box = state.boxes.find((b) => b.index === boxIndex);
      if (!box) return state;
      const expected = expectedBoxWeightG(state, boxIndex);
      const result = checkWeight(expected, event.grams, state.tolerance);
      const boxes = state.boxes.map((b) =>
        b.index === boxIndex ? { ...b, weight: result, weightFlag: result.flag } : b,
      );
      const next: PackState = { ...state, boxes };

      // §15 — LABEL_FIRST: the weigh is a DISCREPANCY CHECK vs the theoretical
      // weight the label was printed with. It NEVER blocks (the label already
      // exists) and NEVER creates a label at the bench; out-of-tolerance is
      // flagged + logged (supervisor may void+reprint where the carrier bills on
      // weight). The order completes via COMPLETE_PACK once every box is weighed.
      if (state.mode === "LABEL_FIRST") {
        const discrepancy = boxes.some((b) => b.weightFlag != null && b.weightFlag !== "OK");
        return {
          ...next,
          weightDiscrepancy: discrepancy,
          cue: result.withinTolerance ? "WEIGHT_OK" : "WEIGHT_DISCREPANCY",
          message: result.withinTolerance
            ? null
            : `Weight discrepancy: label printed for ${expected}g, weighed ${event.grams}g (${result.deltaPercent.toFixed(1)}%). Logged — void+reprint if the carrier bills on exact weight.`,
        };
      }

      if (result.withinTolerance) {
        // §14.1 — in tolerance → (auto webcam capture) → LABELLING. We advance
        // only when every box has a passing/overridden weight (multi-box §14.2).
        const allBoxesOk = boxes.every((b) => b.weightFlag === "OK" || b.weightFlag === "MANUAL");
        return {
          ...next,
          phase: allBoxesOk ? "LABELLING" : "WEIGHING",
          cue: "WEIGHT_OK",
          message: null,
        };
      }
      // §14.1 — out of tolerance → block, offer re-weigh / re-check / override.
      return {
        ...next,
        cue: "WEIGHT_FAIL",
        message: `Expected ${expected}g ±${result.toleranceG}g, got ${event.grams}g (${result.deltaPercent.toFixed(1)}%)`,
      };
    }

    case "WEIGHT_OVERRIDE": {
      // §14.1 — supervisor override (PIN+reason enforced upstream); flags the
      // failing box(es) MANUAL and proceeds to LABELLING.
      if (state.phase !== "WEIGHING") return state;
      const boxes = state.boxes.map((b) =>
        b.weightFlag === "OK" || b.weightFlag === "MANUAL"
          ? b
          : { ...b, weightFlag: "MANUAL" as WeightFlag },
      );
      const allBoxesResolved = boxes.every((b) => b.weightFlag !== null);
      return {
        ...state,
        boxes,
        weightOverridden: true,
        phase: allBoxesResolved ? "LABELLING" : "WEIGHING",
        cue: "WEIGHT_OK",
        message: null,
      };
    }

    case "PHOTO_CAPTURED":
      // §13.4 — informational; capture happens around weight-pass. No phase move.
      return state;

    case "COMPLETE_PACK": {
      // §15/§42.2 — LABEL_FIRST completion at the bench: the label is pre-printed,
      // so there is NO carrier call here. Requires the label confirmed (§33.1) and
      // every box weighed (the discrepancy check ran). The tracking shown is the
      // pre-printed one; fulfilment is pushed per order upstream once PACKED (§40.4).
      if (state.phase !== "WEIGHING" || state.mode !== "LABEL_FIRST") return state;
      if (!state.labelMatched) {
        return { ...state, cue: "LABEL_MISMATCH", message: "Confirm the pre-printed label before completing." };
      }
      const allWeighed = state.boxes.length > 0 && state.boxes.every((b) => b.weightFlag !== null);
      if (!allWeighed) {
        return { ...state, cue: "WEIGHT_FAIL", message: "Weigh every box before completing." };
      }
      return {
        ...state,
        phase: "DONE",
        trackingNumber: state.expectedTracking,
        cue: "LABEL_PRINTED",
        message: null,
        labelError: null,
      };
    }

    case "LABEL_OK": {
      if (state.phase !== "LABELLING") return state;
      // §42.2 — label printed → order done at the bench.
      return {
        ...state,
        phase: "DONE",
        trackingNumber: event.trackingNumber,
        cue: "LABEL_PRINTED",
        message: null,
        labelError: null,
      };
    }

    case "LABEL_FAIL": {
      if (state.phase !== "LABELLING") return state;
      // §14.1 / §42.2(4) — failure surfaced at the bench, pack work preserved.
      return {
        ...state,
        phase: "LABEL_FAILED",
        cue: "LABEL_FAILED",
        message: event.error,
        labelError: event.error,
      };
    }

    case "PARK": {
      // §14.3 — park a half-packed order; lock released, progress reset upstream.
      if (state.phase === "IDLE" || state.phase === "DONE") return state;
      return { ...initialState(state.tolerance), cue: "PARKED", message: event.reason };
    }

    case "MARK_SHORT": {
      // §32 — mark a line short without losing other progress. Records found vs
      // required; the order then routes to AWAITING_CS_DECISION upstream and the
      // station resets cleanly (§32.6 / §35.1).
      if (state.phase !== "VERIFYING" && state.phase !== "WEIGHING") return state;
      const lp = state.lines[event.lineId];
      if (!lp) return state;
      const updatedLine: LinePackState = {
        ...lp,
        foundQuantity: event.foundQuantity,
        shortReasonCode: event.reasonCode,
        // Clamp any already-scanned units down to what was actually found.
        packed: Math.min(lp.packed, event.foundQuantity),
        boxAssignments: lp.boxAssignments.slice(0, Math.min(lp.packed, event.foundQuantity)),
      };
      const lines = { ...state.lines, [event.lineId]: updatedLine };
      // Per §35.1 the order parks for a CS decision and the packer is freed;
      // the station returns to IDLE (§32.6). We keep the short detail on the
      // (about-to-be-cleared) state's message for the UI to log/echo.
      return {
        ...initialState(state.tolerance),
        cue: "SHORT",
        message: `Marked short: line ${event.lineId} ${event.foundQuantity}/${lp.required} (${event.reasonCode})`,
        lines, // exposed so the caller can persist the short detail before reset
      };
    }

    default: {
      // Exhaustiveness guard — every PackEvent variant is handled above.
      const _never: never = event;
      return _never;
    }
  }
}
